import React, { useCallback, useEffect, useState } from 'react';
import { fetchAdminLegalPages, updateAdminLegalPage } from '../services/legalPagesService';
import {
  fetchAdminLegalDocuments,
  updateAdminLegalDocument,
} from '../services/legalDocumentsService';

const TABS = [
  { key: 'terms', label: 'Kullanım Koşulları', kind: 'document', publicPath: '/kullanim-kosullari' },
  { key: 'kvkk_notice', label: 'KVKK Aydınlatma', kind: 'document', publicPath: '/kvkk' },
  { key: 'privacy', label: 'Gizlilik', kind: 'document', publicPath: '/privacy' },
  {
    key: 'technician_confidentiality',
    label: 'Taahhütname',
    kind: 'document',
    publicPath: '/musteri-verileri-taahhut',
  },
  { key: 'acik-riza', label: 'Açık Rıza', kind: 'legacy', publicPath: '/acik-riza' },
];

export default function LegalPagesEditorPage({ variant = 'page' }) {
  const [activeKey, setActiveKey] = useState('terms');
  const [documents, setDocuments] = useState({});
  const [legacyPages, setLegacyPages] = useState({});
  const [baslik, setBaslik] = useState('');
  const [icerik, setIcerik] = useState('');
  const [version, setVersion] = useState('1.0');
  const [aktif, setAktif] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);

  const activeTab = TABS.find((t) => t.key === activeKey) || TABS[0];

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [docRes, legacyRes] = await Promise.all([
        fetchAdminLegalDocuments().catch(() => null),
        fetchAdminLegalPages().catch(() => null),
      ]);
      const docList = docRes?.veri?.belgeler || [];
      const docMap = {};
      docList.forEach((d) => {
        if (!docMap[d.documentType] || d.aktif) {
          docMap[d.documentType] = d;
        }
      });
      setDocuments(docMap);

      const legacyList = legacyRes?.veri?.sayfalar || [];
      const legacyMap = {};
      legacyList.forEach((p) => { legacyMap[p.slug] = p; });
      setLegacyPages(legacyMap);
    } catch (err) {
      setError(err?.message || 'Sayfalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (activeTab.kind === 'document') {
      const d = documents[activeTab.key];
      if (d) {
        setBaslik(d.baslik || '');
        setIcerik(d.icerik || '');
        setVersion(d.version || '1.0');
        setAktif(d.aktif !== false);
      } else {
        setBaslik(activeTab.label);
        setIcerik('');
        setVersion('1.0');
        setAktif(true);
      }
    } else {
      const p = legacyPages[activeTab.key];
      if (p) {
        setBaslik(p.baslik || '');
        setIcerik(p.icerik || '');
        setVersion('');
        setAktif(p.aktif !== false);
      }
    }
  }, [activeKey, documents, legacyPages, activeTab]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      let res;
      if (activeTab.kind === 'document') {
        res = await updateAdminLegalDocument(activeTab.key, {
          baslik,
          icerik,
          version: version || '1.0',
          aktif,
        });
      } else {
        res = await updateAdminLegalPage(activeTab.key, { baslik, icerik, aktif });
      }
      if (res?.basarili === false) {
        setError(res.mesaj || 'Kaydedilemedi');
        return;
      }
      if (window.showNotification) {
        window.showNotification('success', 'Sayfa kaydedildi');
      }
      await loadAll();
    } catch (err) {
      setError(err?.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const wrapperStyle = variant === 'embedded'
    ? { padding: 0 }
    : { maxWidth: '900px', margin: '0 auto' };

  return (
    <div style={wrapperStyle}>
      <div className="ctp-segmented" style={{ marginBottom: '20px', flexWrap: 'wrap' }} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeKey === t.key}
            className={`ctp-segmented__btn${activeKey === t.key ? ' ctp-segmented__btn--active' : ''}`}
            onClick={() => setActiveKey(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: '#64748b' }}>Yükleniyor…</p>
      ) : (
        <div style={{
          background: '#fff',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '12px',
          padding: '20px',
        }}
        >
          {error && (
            <div style={{ marginBottom: '12px', color: '#dc2626', fontSize: '14px' }}>{error}</div>
          )}

          <label style={{ display: 'block', marginBottom: '16px' }}>
            <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Başlık</span>
            <input
              type="text"
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
            />
          </label>

          {activeTab.kind === 'document' && (
            <label style={{ display: 'block', marginBottom: '16px' }}>
              <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                Sürüm
              </span>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                style={{ width: '160px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
              />
              <span style={{ marginLeft: 10, fontSize: 12, color: '#64748b' }}>
                Sürüm değişince mobil kapı yeniden sorulur
              </span>
            </label>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>İçerik (HTML)</span>
            <button type="button" className="secondary-btn" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => setPreview((p) => !p)}>
              {preview ? 'Düzenle' : 'Önizle'}
            </button>
          </div>

          {preview ? (
            <div
              style={{
                minHeight: '240px',
                padding: '16px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                background: '#f8fafc',
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: icerik }}
            />
          ) : (
            <textarea
              value={icerik}
              onChange={(e) => setIcerik(e.target.value)}
              rows={14}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical',
              }}
            />
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', fontSize: '14px' }}>
            <input type="checkbox" checked={aktif} onChange={(e) => setAktif(e.target.checked)} />
            Yayında
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <a
              href={activeTab.publicPath}
              target="_blank"
              rel="noopener noreferrer"
              className="secondary-btn"
              style={{ textDecoration: 'none' }}
            >
              Public sayfayı aç
            </a>
            <button type="button" className="primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
